/**
 * @component NavigationBarRoutesItem
 * @description A component that renders a navigation bar item with an icon, label, and optional menu.
 * It can be used to create a navigation bar with various items that can be clicked or interacted with.
 * @example
 * <NavigationBarRoutesItem
 *   label="Home"
 *   icon={<HomeIcon />}
 *   onClick={() => console.log('Home clicked')}
 * />
 */
import React from 'react';
import { kebabCase } from 'lodash';
import { styled } from '@mui/material/styles';
import Avatar from '../../../Avatar';
import Box from '../../../Box';
import IconButton from '../../../IconButton';
import Tooltip from '../../../Tooltip';
// Define the styled component
const StyledBox = styled(Box)(({ theme }) => ({
    justifyContent: 'center',
    alignContent: 'center',
    display: 'flex',
    flexWrap: 'wrap'
}));
const StyledIconButton = styled(IconButton, {
    shouldForwardProp: prop => prop !== 'focused' && prop !== 'isMenuOpen'
})(({ theme, selected, isMenuOpen, focused }) => (Object.assign({}, (selected &&
    focused &&
    isMenuOpen && {
    backgroundColor: theme.palette.iconButton.interactive.subtle.menuOpen.background,
    border: `${theme.palette.iconButton.border.width} solid ${theme.palette.iconButton.interactive.subtle.menuOpen.border}`
}))));
const NavigationContent = ({ label, icon, onClick, disabled, id, active, focused, initials, isMenuOpen, handleKeyDown }) => {
    if (icon) {
        return (React.createElement(StyledIconButton, { bdsSize: "rectangle", label: label, focused: !!focused, onClick: onClick, disabled: disabled, id: id, selected: active || focused, "data-testid": `icon-button-${kebabCase(label)}`, "aria-label": `icon-button-${kebabCase(label)}`, isMenuOpen: isMenuOpen, onKeyDown: handleKeyDown, tabIndex: disabled ? -1 : 0, role: "button" }, icon));
    }
    /*
     * If no icon, render Avatar (for profile or fallback)
     * TODO: future improvement: Add a prop to allow the user to pass a custom avatar component
     * to be used instead of the default Avatar component.
     */
    return (React.createElement(Avatar, { bdsType: "interactiveSubtle", bdsSize: "xxlarge", onClick: onClick, disabled: disabled, id: id, selected: active || focused, "data-testid": `avatar-button-${kebabCase(label)}`, "aria-label": `avatar-button-${kebabCase(label)}`, role: "button", onKeyDown: handleKeyDown, tabIndex: disabled ? -1 : 0 }, initials || (label ? label[0] : '?')));
};
export const NavigationBarRoutesItem = ({ active, onClick, icon, label, disabled, id, focused, initials, isMenuOpen, path, isFinalPath, href, openInNewTab }) => {
    const handleKeyDown = (event) => {
        if (disabled)
            return;
        if (event.key === 'Enter' || event.key === ' ') {
            if (onClick && !href) {
                event.preventDefault();
                onClick();
            }
        }
    };
    const handleClick = (e) => {
        if (disabled)
            return;
        if (onClick && !href) {
            e.preventDefault();
            onClick();
        }
    };
    const content = (React.createElement(NavigationContent, Object.assign({}, { label, icon, onClick, disabled, id, active, focused, initials, isMenuOpen, handleKeyDown })));
    const renderLinkOrContent = () => {
        if (disabled) {
            return content;
        }
        if (isFinalPath || href || path) {
            return (React.createElement("a", { href: href || path, onClick: handleClick, style: { textDecoration: 'none' }, "aria-label": `Navigate to ${label}`, 
                /**
                 * We set the tabIndex to -1 to prevent the link from being focusable
                 * The focus is handled above by the NavigationBarRoutesItem component
                 */
                tabIndex: -1, onKeyDown: handleKeyDown, target: openInNewTab ? '_blank' : undefined, rel: openInNewTab ? 'noopener noreferrer' : undefined }, content));
        }
        return content;
    };
    return (React.createElement(StyledBox, { "data-testid": "navigation-bar-routes-item" },
        React.createElement(Tooltip, { title: label, placement: "right", arrow: true },
            React.createElement("span", null, renderLinkOrContent()))));
};
//# sourceMappingURL=NavigationBarRoutesItem.js.map